import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FiDownload, FiRefreshCw } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import useVerifyAccess from 'hooks/useVerifyAccess';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import {
  getInventorySalesByStoreExportPage,
  getInventorySalesByStoreReport,
} from 'services/retail-report.service';
import SalesReportFilters, { getSalesReportQuery } from './SalesReportFilters';
import { buildInventorySalesExportSheet } from './inventorySalesExport';
import {
  ReportContextBar,
  ReportPageHeader,
  ReportPageLayout,
  ReportState,
  ReportTableToolbar,
} from './ReportComponents';
import './retailReports.scss';

const formatNumber = (value) => new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 4 }).format(Number(value) || 0);
const formatDate = (value) => {
  if (!value) return '—';
  const [year, month, day] = value.split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
};

const InventorySalesByStoreReportPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { verifyPermission } = useVerifyAccess();
  const canExport = verifyPermission('SL_SALES_REPORT_EXPORT');
  const query = useMemo(() => ({ ...getSalesReportQuery(location.search), items_per_page: 50 }), [location.search]);
  const [portalScope, setPortalScope] = useState(() => getPortalScope());
  const [data, setData] = useState({ summary: {}, stores: [], items: [] });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleScopeChange = (event) => setPortalScope(event?.detail || getPortalScope());
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, handleScopeChange);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, handleScopeChange);
  }, []);

  const load = useCallback(async () => {
    if (!portalScope.company_id) {
      setData({ summary: {}, stores: [], items: [] });
      return;
    }
    setLoading(true);
    setError('');
    try {
      setData(await getInventorySalesByStoreReport(query));
    } catch (requestError) {
      setError(requestError?.message || 'Không thể tải báo cáo tồn kho và xuất bán.');
    } finally {
      setLoading(false);
    }
  }, [portalScope.company_id, query]);

  useEffect(() => { load(); }, [load]);

  const changePage = (page) => {
    const params = new URLSearchParams(location.search);
    params.set('page', page);
    history.push({ pathname: location.pathname, search: params.toString() });
  };

  const exportExcel = async () => {
    if (!portalScope.company_id || exporting) return;
    setExporting(true);
    setError('');
    try {
      const firstPage = await getInventorySalesByStoreExportPage({ ...query, page: 1, items_per_page: 500 });
      const stores = firstPage?.stores || [];
      const totalItems = Number(firstPage?.summary?.totalitems) || 0;
      const allItems = [...(firstPage?.items || [])];
      const totalPages = Math.ceil(totalItems / 500);
      for (let page = 2; page <= totalPages; page += 1) {
        const nextPage = await getInventorySalesByStoreExportPage({ ...query, page, items_per_page: 500 });
        allItems.push(...(nextPage?.items || []));
      }
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, buildInventorySalesExportSheet(allItems, stores), 'Tồn kho - Xuất bán');
      XLSX.writeFile(workbook, `ton-kho-xuat-ban-${query.date_from}-${query.date_to}.xlsx`);
    } catch (requestError) {
      setError(requestError?.message || 'Không thể xuất báo cáo Excel.');
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary || {};
  const stores = data?.stores || [];
  const items = data?.items || [];
  const totalItems = Number(summary.totalitems) || 0;
  const pageSize = Number(summary.itemsperpage) || 50;
  const currentPage = Number(summary.page) || query.page;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  return (
    <ReportPageLayout reportId='inventory-sales-by-store'>
      <ReportPageHeader
        title='Tồn kho và xuất bán theo chi nhánh'
        description='Tồn cuối kỳ tại ngày kết thúc; xuất bán lấy theo phiếu xuất thực tế của đơn bán và không tính chuyển kho.'
        actions={(
          <button type='button' className='bc_rr_action_button is-secondary' onClick={load} disabled={loading || !portalScope.company_id}>
            <FiRefreshCw aria-hidden='true' /> Làm mới
          </button>
        )}
      />
      <ReportContextBar items={[
        { label: 'Phạm vi', value: `${Number(summary.totalstores) || 0} chi nhánh được phân quyền` },
        { label: 'Kỳ', value: `${formatDate(query.date_from)}–${formatDate(query.date_to)}` },
        { label: 'Xuất bán', value: 'Chỉ phiếu xuất của đơn bán' },
      ]} />
      <SalesReportFilters
        includeKeywordFilter
        keywordLabel='Tìm sản phẩm'
        keywordPlaceholder='Mã hàng, tên hàng, ngành hàng'
        loading={loading}
      />

      <ReportTableToolbar
        summary={(
          <><strong>{formatNumber(totalItems)} sản phẩm</strong><span>Tổng tồn: <b>{formatNumber(summary.totalinventory)}</b></span><span>Tổng bán: <b>{formatNumber(summary.totalsold)}</b></span></>
        )}
        actions={(
          <button type='button' className='is-primary' onClick={exportExcel} disabled={!canExport || exporting || !totalItems}>
            <FiDownload aria-hidden='true' /> {exporting ? 'Đang xuất Excel' : 'Xuất Excel'}
          </button>
        )}
      />

      {error ? <ReportState tone='danger' title='Không tải được báo cáo' description={error} actionLabel='Thử lại' onAction={load} /> : null}
      {!error && loading ? <ReportState title='Đang tổng hợp dữ liệu' description='Đang tính tồn cuối kỳ và xuất bán của các chi nhánh được phân quyền.' /> : null}
      {!error && !loading && !portalScope.company_id ? <ReportState title='Chưa chọn công ty' description='Chọn công ty ở thanh trên cùng để xem báo cáo.' /> : null}
      {!error && !loading && portalScope.company_id && !items.length ? <ReportState title='Không có dữ liệu' description='Không có tồn kho hoặc xuất bán trong phạm vi và khoảng ngày đã chọn.' /> : null}

      {!error && !loading && items.length ? (
        <section className='bc_rr_matrix_panel'>
          <div className='bc_rr_matrix_wrap'>
            <table className='bc_rr_matrix_table'>
              <thead>
                <tr>
                  <th rowSpan='2' className='is-sticky is-category'>Ngành hàng</th>
                  <th rowSpan='2' className='is-sticky is-code'>Mã hàng</th>
                  <th rowSpan='2' className='is-sticky is-product'>Tên hàng</th>
                  <th rowSpan='2' className='is-sticky is-brand'>Thương hiệu</th>
                  <th rowSpan='2' className='is-sticky is-unit'>Đơn vị tính</th>
                  {stores.map((store) => <th key={store.storeid} colSpan='2' className='is-store'>{store.storecode || store.storename}</th>)}
                  <th rowSpan='2' className='is-total'>Tổng tồn</th>
                  <th rowSpan='2' className='is-total'>Tổng bán</th>
                </tr>
                <tr>{stores.flatMap((store) => [<th key={`${store.storeid}-inventory`}>Tồn cuối kỳ</th>, <th key={`${store.storeid}-sales`}>Xuất bán</th>])}</tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.productid}>
                    <td className='is-sticky is-category' title={item.categoryname}>{item.categoryname}</td>
                    <td className='is-sticky is-code'><strong>{item.productcode || '—'}</strong></td>
                    <td className='is-sticky is-product' title={item.productname}>{item.productname || '—'}</td>
                    <td className='is-sticky is-brand' title={item.manufacturername}>{item.manufacturername || '—'}</td>
                    <td className='is-sticky is-unit'>{item.unitname || '—'}</td>
                    {stores.flatMap((store) => {
                      const cell = item.stores?.[`${store.storeid}`] || {};
                      return [
                        <td key={`${store.storeid}-inventory`} className='is-number'>{formatNumber(cell.closing_inventory)}</td>,
                        <td key={`${store.storeid}-sales`} className='is-number is-sales'>{formatNumber(cell.sold_quantity)}</td>,
                      ];
                    })}
                    <td className='is-number is-total'>{formatNumber(item.closinginventory)}</td>
                    <td className='is-number is-total is-sales'>{formatNumber(item.soldquantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className='bc_rr_matrix_pagination'>
            <span>Trang {currentPage}/{totalPages}</span>
            <div><button type='button' disabled={currentPage <= 1} onClick={() => changePage(currentPage - 1)}>Trang trước</button><button type='button' disabled={currentPage >= totalPages} onClick={() => changePage(currentPage + 1)}>Trang sau</button></div>
          </div>
        </section>
      ) : null}
    </ReportPageLayout>
  );
};

export default InventorySalesByStoreReportPage;
